import { isFunction } from 'lodash-es';
import { useCallback } from 'react';
import { useMixpanel } from '../../contexts';
import { MixpanelEvent } from '../../tokens';
import { createExportFileName } from '../../utils';
import { Button } from '../Button';
import { FormControlSizes } from '../Form';
import { IconName } from '../Icons';
import type { UseBlotterTable } from './useBlotterTable';
import { safeGridApi, type ExportGridMode } from './utils';

interface ExportMenuItemsProps<T> {
  handleClose?: () => void;
  blotterTable: Pick<UseBlotterTable<T>, 'gridApi' | 'exportOptions' | 'exportDataAsCSV' | 'exportDataAsExcel'>;
  csvTestId?: string;
  excelTestId?: string;
}

/**
 * Renders export menu items for a blotter table.
 * For export functionality to be available, the blotterTable's `exportOptions` property must be configured.
 * @param handleClose - Optional callback to close the export menu.
 * @param blotterTable - The blotter table to export.
 * @param csvTestId - Optional data-testid for the CSV export button.
 * @param excelTestId - Optional data-testid for the Excel export button.
 * @returns A React component that renders the export menu items.
 */
export const ExportMenuItems = <T,>({ handleClose, blotterTable, csvTestId, excelTestId }: ExportMenuItemsProps<T>) => {
  const mixpanel = useMixpanel();

  const { baseParams, csvParams, excelParams, name, tabLabel } = blotterTable.exportOptions || {};

  const handleExport = useCallback(
    (mode: ExportGridMode) => {
      if (!name) {
        console.warn('Export functionality requires exportOptions to be configured');
        return;
      }
      mixpanel.track(mode === 'CSV' ? MixpanelEvent.ExportRows : MixpanelEvent.ExportRowsToExcel);
      const defaultParams: {
        fileName: string;
      } = {
        fileName: createExportFileName({
          name,
          tabLabel,
          mode,
        }),
      };

      if (mode === 'CSV') {
        blotterTable.exportDataAsCSV({
          ...defaultParams,
          ...(isFunction(baseParams) ? baseParams(safeGridApi(blotterTable.gridApi)) : baseParams),
          ...csvParams,
        });
      } else if (mode === 'Excel') {
        blotterTable.exportDataAsExcel({
          ...defaultParams,
          ...(isFunction(baseParams) ? baseParams(safeGridApi(blotterTable.gridApi)) : baseParams),
          ...excelParams,
        });
      }
      handleClose?.();
    },
    [name, mixpanel, tabLabel, handleClose, blotterTable, baseParams, csvParams, excelParams]
  );

  return (
    <>
      <Button
        startIcon={IconName.DocumentDownload}
        size={FormControlSizes.Small}
        onClick={() => handleExport('CSV')}
        data-testid={csvTestId ?? 'export-csv'}
      >
        Export CSV
      </Button>
      <Button
        startIcon={IconName.DocumentDownload}
        size={FormControlSizes.Small}
        onClick={() => handleExport('Excel')}
        data-testid={excelTestId ?? 'export-excel'}
      >
        Export Excel
      </Button>
    </>
  );
};
